import React, { useState } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import { useNotification } from '../contexts/NotificationContext';
import './Achievements.css';

const Achievements = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showProgressManager, setShowProgressManager] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const { 
    isAchievementEarned, 
    isAchievementEligible, 
    claimAchievement, 
    exportProgress, 
    importProgress,
    exportProgressString,
    importProgressString,
    resetAllProgress,
    progress,
    getConceptBadgeData
  } = useProgress();
  const { notifySuccess, notifyError, openConfirmModal } = useNotification();

  // Update achievement earned status dynamically
  const updateAchievementEarnedStatus = (achievements) => {
    return achievements.map(achievement => ({
      ...achievement,
      earned: isAchievementEarned(achievement.id)
    }));
  };

  // Export progress handler
  const handleExport = () => {
    try {
      exportProgress();
      notifySuccess('Progress Exported!', 'Your progress has been downloaded successfully.');
    } catch (error) {
      notifyError('Export Failed', 'Unable to export your progress. Please try again.');
    }
  };

  // Import progress handler
  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const result = importProgress(e.target.result);
        if (result.success) {
          notifySuccess('Progress Imported!', result.message);
        } else {
          notifyError('Import Failed', result.message);
        }
      } catch (error) {
        notifyError('Invalid File', 'The file format is invalid or corrupted.');
      }
    };
    reader.readAsText(file);
    
    // Reset input
    event.target.value = '';
  };

  // Copy backup code to clipboard
  const handleCopyCode = () => {
    const code = exportProgressString();
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        setBackupCode(code);
        notifySuccess('Backup Code Copied!', 'Your backup code has been copied to clipboard.');
      }).catch(() => {
        setBackupCode(code);
        notifyError('Copy Failed', 'Failed to copy. The code is displayed below for manual copying.');
      });
    } else {
      notifyError('No Progress', 'There is no progress data to export.');
    }
  };

  // Import from backup code
  const handleImportCode = () => {
    const code = document.getElementById('backup-code-input').value.trim();
    if (!code) {
      notifyError('Missing Code', 'Please enter a backup code to import.');
      return;
    }

    const result = importProgressString(code);
    if (result.success) {
      notifySuccess('Progress Restored!', result.message);
      setShowCodeInput(false);
      document.getElementById('backup-code-input').value = '';
    } else {
      notifyError('Import Failed', result.message);
    }
  };

  // Reset progress
  const handleReset = async () => {
    const confirmed = await openConfirmModal({
      type: 'danger',
      title: 'Reset All Progress?',
      body: 'Are you sure you want to permanently delete all your progress, achievements, and learning history? This action cannot be undone.',
      confirmLabel: 'Reset All Progress',
      cancelLabel: 'Keep My Progress'
    });

    if (confirmed) {
      const result = resetAllProgress();
      if (result.success) {
        notifySuccess('Progress Reset', result.message);
      } else {
        notifyError('Reset Failed', result.message);
      }
    }
  };

  const achievementCategories = {
    games: {
      title: 'Game Masters',
      subtitle: 'Complete games and master Miden concepts.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: [
        {
          id: 'challenger',
          title: 'First Game',
          description: 'Complete your first game',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" strokeWidth="2"/>
              <path d="M32 20 L36 28 L45 29 L38 35 L40 44 L32 39 L24 44 L26 35 L19 29 L28 28 Z" fill="#f97316"/>
            </svg>
          )
        },
        {
          id: 'warrior',
          title: 'Game Warrior',
          description: 'Complete 5 games',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" strokeWidth="2"/>
              <path d="M22 24 L28 18 L28 34" stroke="#ef4444" strokeWidth="2"/>
              <path d="M42 24 L36 18 L36 34" stroke="#ef4444" strokeWidth="2"/>
              <circle cx="28" cy="36" r="2" fill="#f97316"/>
              <circle cx="36" cy="36" r="2" fill="#f97316"/>
            </svg>
          )
        },
        {
          id: 'maze-runner',
          title: 'Maze Runner',
          description: 'Complete all Logic Maze levels',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" strokeWidth="2"/>
              <rect x="22" y="20" width="20" height="20" rx="2" stroke="#475569" strokeWidth="1.5" fill="none"/>
              <path d="M24 22 L26 22 L26 26 L30 26 L30 22 L34 22 L34 30 L38 30 L38 38 L40 38" stroke="#f97316" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="40" cy="38" r="1.5" fill="#f97316"/>
              <circle cx="24" cy="22" r="1" fill="#10b981"/>
            </svg>
          )
        },
        {
          id: 'no-help-hero',
          title: 'No-Help Hero',
          description: 'Finish Logic Maze without using any hints',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M32 8 L44 16 L44 36 L32 44 L20 36 L20 16 Z" fill="rgba(15,23,42,0.8)" stroke="#f97316" strokeWidth="2"/>
              <circle cx="32" cy="28" r="8" stroke="#8b5cf6" strokeWidth="2" fill="none"/>
              <path d="M30 26 Q32 24 34 26" stroke="#8b5cf6" strokeWidth="1.5" fill="none"/>
              <circle cx="32" cy="34" r="1" fill="#8b5cf6"/>
              <line x1="24" y1="20" x2="40" y2="36" stroke="#ef4444" strokeWidth="2.5"/>
            </svg>
          )
        }
      ]
    },
    tutorials: {
      title: 'Learning Path',
      subtitle: 'Follow the tutorials and expand your knowledge.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: [
        {
          id: 'tutorial-complete',
          title: 'Tutorial Graduate',
          description: 'Complete your first tutorial',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <circle cx="32" cy="32" r="28" fill="rgba(15,23,42,0.8)" stroke="#14b8a6" strokeWidth="2"/>
              <path d="M20 36 L28 36 L28 32 L36 32 L36 36 L44 36" stroke="#14b8a6" strokeWidth="1.5" fill="none"/>
              <path d="M26 28 L32 22 L38 28 L38 24 L32 18 L26 24 Z" fill="#14b8a6"/>
              <rect x="29" y="36" width="6" height="4" rx="1" fill="#14b8a6" opacity="0.6"/>
            </svg>
          )
        },
        {
          id: 'advanced-student',
          title: 'Advanced Student',
          description: 'Complete 5 tutorials',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <circle cx="32" cy="32" r="28" fill="rgba(15,23,42,0.8)" stroke="#14b8a6" strokeWidth="2"/>
              <path d="M20 38 L28 38 L28 34 L36 34 L36 38 L44 38" stroke="#14b8a6" strokeWidth="1.5" fill="none"/>
              <path d="M26 30 L26 22 L30 18 L34 18 L38 22 L38 30" stroke="#8b5cf6" strokeWidth="2" fill="none"/>
              <path d="M28 24 L32 20 L36 24" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )
        }
      ]
    },
    streaks: {
      title: 'Consistency',
      subtitle: 'Build your learning streak and stay committed.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: [
        {
          id: 'week-warrior',
          title: 'Week Warrior',
          description: 'Maintain a 7-day streak',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M16 12 L48 12 L52 32 L48 52 L16 52 L12 32 Z" fill="rgba(15,23,42,0.8)" stroke="#ef4444" strokeWidth="2"/>
              <path d="M32 18 L28 28 L36 28 L30 38 L34 38 L38 28 L32 28 Z" fill="#f97316"/>
              <circle cx="24" cy="24" r="1" fill="#f97316" opacity="0.5"/>
              <circle cx="40" cy="24" r="1" fill="#f97316" opacity="0.5"/>
              <circle cx="24" cy="40" r="1" fill="#f97316" opacity="0.5"/>
              <circle cx="40" cy="40" r="1" fill="#f97316" opacity="0.5"/>
            </svg>
          )
        },
        {
          id: 'month-master',
          title: 'Month Master',
          description: 'Maintain a 30-day streak',
          earned: false,
          icon: (
            <svg width="100%" height="100%" viewBox="8 8 48 48" fill="none">
              <path d="M16 12 L48 12 L52 32 L48 52 L16 52 L12 32 Z" fill="rgba(15,23,42,0.8)" stroke="#f59e0b" strokeWidth="2"/>
              <path d="M24 38 L24 44 L40 44 L40 38 L38 36 L26 36 Z" fill="#f59e0b"/>
              <rect x="26" y="30" width="12" height="6" rx="1" fill="#f59e0b"/>
              <path d="M28 24 L32 20 L36 24" stroke="#f59e0b" strokeWidth="2" fill="none"/>
              <path d="M26 18 L28 22 L30 18 L32 22 L34 18 L36 22 L38 18" stroke="#f59e0b" strokeWidth="1.5" fill="none"/>
            </svg>
          )
        }
      ]
    },
    'private-payments': {
      title: 'Private Payments',
      subtitle: 'Master the art of private transactions.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="8" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 8V6a5 5 0 0 1 10 0v2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'account-recovery': {
      title: 'Account Recovery',
      subtitle: 'Become a state detective and recover accounts.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
          <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'secure-receipts': {
      title: 'Secure Receipts',
      subtitle: 'Build and verify secure transaction receipts.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
          <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'private-proofs': {
      title: 'Private Proofs',
      subtitle: 'Master zero-knowledge proof construction.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'privacy-protection': {
      title: 'Privacy Protection',
      subtitle: 'Guard privacy in transaction flows.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'privacy-basics': {
      title: 'Privacy Basics',
      subtitle: 'Learn the fundamentals of privacy.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" stroke="currentColor" strokeWidth="2"/>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'client-side-proving': {
      title: 'Client-Side Proving',
      subtitle: 'Master proving on the client side.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    },
    'transaction-flow': {
      title: 'Transaction Flow',
      subtitle: 'Navigate transaction flows like a pro.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M13 7l5 5-5 5M6 7l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      achievements: []
    },
    'invisible-math': {
      title: 'Invisible Math',
      subtitle: 'Master the mathematics behind privacy.',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m10.6 0l-4.24-4.24M2 12h6m8 0h6" stroke="currentColor" strokeWidth="2"/>
        </svg>
      ),
      achievements: []
    }
  };

  // Generate concept badges dynamically for each concept category
  const conceptBadgeMapping = {
    'private-payments': ['private-payments-rookie', 'private-payments-apprentice', 'private-payments-master', 'private-payments-expert'],
    'account-recovery': ['account-recovery-rookie', 'account-recovery-apprentice', 'account-recovery-master', 'account-recovery-expert'],
    'secure-receipts': ['secure-receipts-rookie', 'secure-receipts-apprentice', 'secure-receipts-master', 'secure-receipts-expert'],
    'private-proofs': ['private-proofs-rookie', 'private-proofs-apprentice', 'private-proofs-master', 'private-proofs-expert'],
    'privacy-protection': ['privacy-protection-rookie', 'privacy-protection-apprentice', 'privacy-protection-master', 'privacy-protection-expert'],
    'privacy-basics': ['privacy-basics-rookie', 'privacy-basics-apprentice', 'privacy-basics-master', 'privacy-basics-expert'],
    'client-side-proving': ['client-side-proving-rookie', 'client-side-proving-apprentice', 'client-side-proving-master', 'client-side-proving-expert'],
    'transaction-flow': ['transaction-flow-rookie', 'transaction-flow-apprentice', 'transaction-flow-master', 'transaction-flow-expert'],
    'invisible-math': ['invisible-math-rookie', 'invisible-math-apprentice', 'invisible-math-master', 'invisible-math-expert']
  };

  // Populate concept badges for each category
  if (getConceptBadgeData) {
    Object.entries(conceptBadgeMapping).forEach(([categoryKey, badgeIds]) => {
      if (achievementCategories[categoryKey]) {
        achievementCategories[categoryKey].achievements = badgeIds.map(badgeId => {
          const badgeData = getConceptBadgeData(badgeId);
          if (badgeData) {
            return {
              id: badgeId,
              title: badgeData.title,
              description: badgeData.description,
              earned: isAchievementEarned ? isAchievementEarned(badgeId) : false,
              icon: badgeData.icon,
              rarity: badgeData.rarity,
              concept: badgeData.concept
            };
          }
          return null;
        }).filter(Boolean);
      }
    });
  }

  const tabs = [
    { id: 'all', label: 'All Achievements' },
    { id: 'earned', label: 'Your Achievements' }
  ];

  const categories = Object.keys(achievementCategories);

  const getFilteredAchievements = () => {
    let allAchievements = [];
    categories.forEach(categoryId => {
      const category = achievementCategories[categoryId];
      const updatedAchievements = updateAchievementEarnedStatus(category.achievements);
      updatedAchievements.forEach(achievement => {
        allAchievements.push({
          ...achievement,
          category: categoryId,
          categoryTitle: category.title
        });
      });
    });

    if (activeTab === 'earned') {
      return allAchievements.filter(achievement => achievement.earned);
    }
    return allAchievements;
  };

  const getEarnedCount = () => {
    return getFilteredAchievements().filter(a => a.earned).length;
  };

  // Get all concept badges earned by user
  const getConceptBadgesEarned = () => {
    if (!progress || !progress.concepts) return [];
    const badges = [];
    Object.entries(progress.concepts).forEach(([concept, data]) => {
      if (data.badges && Array.isArray(data.badges)) {
        data.badges.forEach(badgeId => {
          const badgeData = getConceptBadgeData ? getConceptBadgeData(badgeId) : null;
          if (badgeData) {
            badges.push({
              ...badgeData,
              earned: true,
              concept
            });
          }
        });
      }
    });
    return badges;
  };

  return (
    <section className="achievements-page">
      <div className="achievements-header">
        <h1 className="achievements-title">Achievements</h1>
        <div className="level-indicator">
          <span className="level-text">Level {Math.floor(getEarnedCount() / 3) + 1}</span>
          <div className="level-progress">
            <div 
              className="level-progress-fill" 
              style={{width: `${(getEarnedCount() % 3) * 33.33}%`}}
            ></div>
          </div>
        </div>
      </div>

      <div className="achievement-tabs">
        {tabs.map(tab => (
          <button 
            key={tab.id}
            className={`achievement-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Progress Management Section */}
      <div className="progress-manager-container">
        <button 
          className="progress-manager-toggle"
          onClick={() => setShowProgressManager(!showProgressManager)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
            <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2"/>
            <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Backup & Restore
        </button>

        {showProgressManager && (
          <div className="progress-manager-panel">
            <div className="progress-manager-header">
              <h3>Progress Backup & Restore</h3>
              <p>Your progress is saved locally to your browser. Use these tools to backup and restore your data.</p>
            </div>

            <div className="progress-stats">
              <div className="stat-item">
                <span className="stat-label">Achievements</span>
                <span className="stat-value">{Object.keys(progress.achievements || {}).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Games Progress</span>
                <span className="stat-value">{Object.keys(progress.games || {}).length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Tutorials</span>
                <span className="stat-value">{Object.keys(progress.tutorials || {}).length}</span>
              </div>
            </div>

            <div className="progress-actions">
              <button className="export-button" onClick={handleExport}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Download File
              </button>

              <label className="import-button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Upload File
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={handleImport}
                  style={{ display: 'none' }}
                />
              </label>

              <button className="copy-code-button" onClick={handleCopyCode}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Copy Code
              </button>

              <button className="paste-code-button" onClick={() => setShowCodeInput(!showCodeInput)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 2h6a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v0a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Paste Code
              </button>
            </div>

            {showCodeInput && (
              <div className="code-input-section">
                <label htmlFor="backup-code-input" className="code-label">
                  Enter Backup Code:
                </label>
                <textarea 
                  id="backup-code-input"
                  className="code-textarea"
                  placeholder="Paste your backup code here..."
                  rows="3"
                />
                <button className="import-code-button" onClick={handleImportCode}>
                  Restore from Code
                </button>
              </div>
            )}

            {backupCode && (
              <div className="backup-code-display">
                <label className="code-label">Your Backup Code:</label>
                <textarea 
                  className="code-textarea"
                  value={backupCode}
                  readOnly
                  rows="3"
                  onClick={(e) => e.target.select()}
                />
                <p className="code-hint">Save this code to restore your progress on another device</p>
              </div>
            )}

            <div className="progress-warning">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Progress does not sync across devices. Export your data to transfer it to another browser or device.</span>
            </div>

            <div className="security-warning">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="12" r="1" fill="currentColor"/>
              </svg>
              <span><strong>Security Notice:</strong> Only import progress files or codes that you exported from this app. Never import files from untrusted sources.</span>
            </div>

            <div className="danger-zone">
              <h4 className="danger-title">Danger Zone</h4>
              <p className="danger-description">Reset all progress permanently. This action cannot be undone.</p>
              <button className="reset-button" onClick={handleReset}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <polyline points="1 4 1 10 7 10" stroke="currentColor" strokeWidth="2"/>
                  <polyline points="23 20 23 14 17 14" stroke="currentColor" strokeWidth="2"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2"/>
                </svg>
                Reset All Progress
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="achievements-content">
        {categories.map(categoryId => {
          const category = achievementCategories[categoryId];
          const categoryAchievements = getFilteredAchievements().filter(
            achievement => achievement.category === categoryId
          );
          
          if (categoryAchievements.length === 0) return null;

          return (
            <div key={categoryId} className="achievement-category">
              <div className="category-header">
                <div className="category-icon">
                  {category.icon}
                </div>
                <div className="category-info">
                  <h3 className="category-title">{category.title}</h3>
                  <p className="category-subtitle">{category.subtitle}</p>
                </div>
              </div>
              
              <div className="achievements-badges-row">
                {categoryAchievements.map((achievement) => (
                  <div key={achievement.id} className={`achievement-badge-card ${achievement.earned ? 'claimed' : 'unclaimed'}`}>
                    <div className={`badge-icon ${achievement.earned ? 'earned' : 'locked'}`}>
                      {achievement.earned ? (
                        <div className="achievement-emoji">{achievement.icon}</div>
                      ) : (
                        <div className="achievement-emoji locked">{achievement.icon}</div>
                      )}
                      {!achievement.earned && (
                        <div className="lock-overlay">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <h4 className="achievement-title">{achievement.title}</h4>
                    <p className="achievement-description">{achievement.description}</p>
                    <div className="achievement-status">
                      {achievement.earned ? (
                        <div className="claimed-button">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                          Claimed
                        </div>
                      ) : isAchievementEligible(achievement.id) ? (
                        <button className="claim-button" onClick={() => claimAchievement(achievement.id)}>Claim</button>
                      ) : (
                        <button className="claim-button" disabled>Locked</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Concept Badges Section */}
        {getConceptBadgesEarned().length > 0 && (
          <div className="achievement-category">
            <div className="category-header">
              <div className="category-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="category-info">
                <h3 className="category-title">Concept Badges</h3>
                <p className="category-subtitle">Badges earned by mastering concepts through games and tutorials.</p>
              </div>
            </div>
            
            <div className="achievements-badges-row">
              {getConceptBadgesEarned().map((badge) => (
                <div key={badge.id} className="achievement-badge-card claimed">
                  <div className="badge-icon earned">
                    <div className="achievement-emoji">{badge.icon}</div>
                  </div>
                  <h4 className="achievement-title">{badge.title}</h4>
                  <p className="achievement-description">{badge.description}</p>
                  <div className="achievement-status">
                    <div className="claimed-button">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Earned
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Achievements;
